/**
 * Care-team messaging.
 *
 * One Messages drawer with two teams — Providers (the Healthie thread with
 * the patient's physician + nurse) and System Support (the AI-first support
 * chat) — replacing the separate clinic MessageDrawer and support Panel for
 * every patient. This module owns the drawer's UI state; it lives
 * outside React (like `messageDrawer` / `supportChat`) so any surface can
 * open it without prop-drilling.
 */

import { useSyncExternalStore } from 'react';

import { track } from '@/lib/analytics';

export function useCareTeamMessaging(): boolean {
  return true;
}

export type Team = 'providers' | 'support';

export type SupportView =
  | { kind: 'chat' }
  | { kind: 'requests' }
  | { kind: 'request'; id: string; from?: 'requests' };

export interface MessagingHubState {
  open: boolean;
  /** null = the "who would you like to reach?" chooser. */
  team: Team | null;
  supportView: SupportView;
}

let state: MessagingHubState = {
  open: false,
  team: null,
  supportView: { kind: 'chat' },
};
const listeners = new Set<() => void>();

function set(patch: Partial<MessagingHubState>) {
  state = { ...state, ...patch };
  listeners.forEach((l) => l());
}

export const messagingHub = {
  open({ team = null, source }: { team?: Team | null; source: string }) {
    set({ open: true, team, supportView: { kind: 'chat' } });
    track('messaging_hub_opened', { source, team: team ?? 'chooser' });
  },
  close() {
    if (!state.open) return;
    set({ open: false });
  },
  selectTeam(team: Team, source: string) {
    set({ team, supportView: { kind: 'chat' } });
    track('messaging_team_selected', { team, source });
  },
  backToChooser() {
    set({ team: null, supportView: { kind: 'chat' } });
  },
  showSupportView(view: SupportView) {
    set({ team: 'support', supportView: view });
    if (view.kind !== 'chat') track('messaging_requests_viewed', { view: view.kind });
  },
};

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useMessagingHub(): MessagingHubState {
  return useSyncExternalStore(subscribe, () => state);
}
